import {
  httpFetch,
  httpFetchJson,
} from '../../../../core/services/api/httpClient';
import { AssistantCreationModel } from '../../../../core/models/assistant/assistantCreationModel.ts';
import { AssistantConfigurationModel } from '../../../../core/models/assistant/assistantConfigurationModel.ts';
import { UpdateUserAssistantAccessRequestBody } from '../../../../core/models/assistant/updateUserAssistantAccessRequestBody.ts';
import { GetModelResponseAsync } from '../../../../core/models/assistant/getModelResponseModel.ts';
import { FavoriteAssistantResponse } from '../../../../core/models/assistant/favoriteAssistantResponse.ts';
import { AssistantCompleteModel, AssistantDetailModel } from '../../types.ts';
import { GroupAccessItem } from '../../../assistantform/utils/assistantFormOptions.ts';

export interface UserMembership {
  userId: string;
  userEmail: string;
  userName?: string;
}

export interface AdminMembership {
  id: string;
  name: string;
  email: string | null;
  type: 'user' | 'group';
}

export interface MembershipsResponse {
  owners: { name: string; id: string; email: string }[];
  admins: AdminMembership[];
  accessUsers: UserMembership[];
  accessGroups: GroupAccessItem[];
}
export interface UpdateMembershipsPayload {
  access_users_id_list?: string[];
  access_groups_id_list?: string[];
  admin_users_id_list?: string[];
  admin_groups_id_list?: string[];
}

const buildQueryString = (
  params: Record<string, string | null | undefined>
): string => {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      searchParams.set(key, value);
    }
  }
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

/**
 * Get the assistant details by Name
 */
const getAssistantDetails = async (
  name: string
): Promise<AssistantDetailModel> => {
  return httpFetchJson<AssistantDetailModel>(
    `/api/assistant/details${buildQueryString({ name })}`,
    { withBackendAuth: true }
  );
};

/**
 * Get the complete assistant list or filtered list by url
 * @param url The assistant url.
 */
const listAssistantsAsync = async (
  type?: string | null,
  url?: string | null | undefined
): Promise<AssistantCompleteModel[]> => {
  const response = await httpFetch(
    `/api/assistants${buildQueryString({ type, url })}`,
    { withBackendAuth: true }
  );
  if (response.status === 204) {
    return [];
  }
  return response.json() as Promise<AssistantCompleteModel[]>;
};

/**
 * Upsert the assistant => when same name exists in the database, upadate. Otherwise, create it.
 * @param newAssistant The assistant body.
 */
const createAssistantAsync = async (
  newAssistant: AssistantCreationModel
): Promise<string> => {
  return httpFetchJson<string>(`/api/assistant/function_upsert`, {
    method: 'POST',
    withBackendAuth: true,
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(newAssistant),
  });
};

const getAssistantCreationConfigurationAsync =
  async (): Promise<AssistantConfigurationModel> => {
    return httpFetchJson<AssistantConfigurationModel>(
      '/api/assistant/form_configurations',
      { withBackendAuth: true }
    );
  };

const createPersonalAssistantAsync = async (
  formData: FormData
): Promise<{ success: boolean; id?: string; name?: string }> => {
  return httpFetchJson<{ success: boolean; id?: string; name?: string }>(
    '/api/assistant/form-upsert',
    {
      method: 'POST',
      withBackendAuth: true,
      body: formData,
    }
  );
};

const updateUserAccessAsync = async (
  body: UpdateUserAssistantAccessRequestBody
) => {
  return httpFetchJson('/api/assistant/update_user_access', {
    method: 'POST',
    withBackendAuth: true,
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
};

const getDefaultModelAsync = async (): Promise<GetModelResponseAsync> => {
  return httpFetchJson<GetModelResponseAsync>('/api/model/defaultModel', {
    withBackendAuth: true,
  });
};

const getAssistantLogo = async (assistantName: string) => {
  const response = await httpFetch(`/api/assistant/logo/${assistantName}`, {
    withBackendAuth: true,
  });
  return response.blob();
};

const getFavoriteAssistants = async (): Promise<FavoriteAssistantResponse> => {
  const response = await httpFetch('/api/assistant/get_favorites', {
    withBackendAuth: true,
  });
  if (response.status === 204) {
    return { favoriteAssistantIds: [] };
  }
  return response.json();
};

const postFavoriteAssistants = async (
  assistantId: string
): Promise<{ success: boolean }> => {
  return httpFetchJson<{ success: boolean }>('/api/assistant/add_favorite', {
    method: 'POST',
    withBackendAuth: true,
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ assistantId }),
  });
};

const deleteFavoriteAssistants = async (
  assistantId: string
): Promise<{ success: boolean }> => {
  return httpFetchJson<{ success: boolean }>(
    `/api/assistant/delete_favorite/${assistantId}`,
    {
      method: 'DELETE',
      withBackendAuth: true,
    }
  );
};

const deleteAssistantAsync = async (
  assistantName: string
): Promise<{ success: boolean }> => {
  return httpFetchJson<{ success: boolean }>(
    `/api/assistant/delete${buildQueryString({ name: assistantName })}`,
    {
      method: 'DELETE',
      withBackendAuth: true,
    }
  );
};

/**
 * Fetch assistant memberships for owner management views.
 */
const getAssistantMembershipsAsync = async (
  assistantId: string
): Promise<MembershipsResponse> => {
  return httpFetchJson<MembershipsResponse>(
    `/api/assistants/${assistantId}/memberships`,
    { withBackendAuth: true }
  );
};

/**
 * Add users to an assistant membership list.
 */
const addAssistantMembershipsAsync = async (
  assistantId: string,
  payload: UpdateMembershipsPayload
): Promise<void> => {
  await httpFetch(`/api/assistants/${assistantId}/memberships`, {
    method: 'POST',
    withBackendAuth: true,
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
};

/**
 * Remove users from an assistant membership list.
 */
const removeAssistantMembershipsAsync = async (
  assistantId: string,
  payload: UpdateMembershipsPayload
): Promise<void> => {
  await httpFetch(`/api/assistants/${assistantId}/memberships`, {
    method: 'DELETE',
    withBackendAuth: true,
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
};

export const assistantAPI = {
  getAssistantDetails,
  listAssistantsAsync,
  createAssistantAsync,
  getAssistantCreationConfigurationAsync,
  createPersonalAssistantAsync,
  updateUserAccessAsync,
  getDefaultModelAsync,
  getAssistantLogo,
  getFavoriteAssistants,
  postFavoriteAssistants,
  deleteFavoriteAssistants,
  deleteAssistantAsync,
  getAssistantMembershipsAsync,
  addAssistantMembershipsAsync,
  removeAssistantMembershipsAsync,
};
